import React, { useState } from 'react';
import { 
  Settings,
  Server,
  Database,
  Shield,
  Mail,
  Globe,
  Clock,
  Cloud,
  Key,
  File,
  AlertTriangle,
  Edit,
  Save,
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
  HelpCircle
} from 'lucide-react';

// Structure de données pour la documentation de configuration
const CONFIGURATION_DOCUMENTATION = {
  metadata: {
    title: "Documentation de Configuration",
    version: "2.0.0",
    lastUpdate: "2024-03-23",
    environment: "Production",
    author: "Équipe Architecture"
  },
  categories: [
    {
      id: "system",
      name: "Configuration Système",
      icon: "Server",
      description: "Paramètres fondamentaux du système",
      configurations: [
        {
          id: "server",
          name: "Configuration Serveur",
          description: "Paramètres du serveur d'application",
          environment: "Toutes",
          parameters: [
            {
              key: "SERVER_PORT",
              type: "number",
              defaultValue: "3000",
              description: "Port d'écoute du serveur",
              required: true,
              validation: "1024-65535",
              example: "3000",
              impact: "Redémarrage requis",
              security: "Interne uniquement"
            },
            {
              key: "MAX_CONNECTIONS",
              type: "number",
              defaultValue: "1000",
              description: "Nombre maximum de connexions simultanées",
              required: true,
              validation: "100-10000",
              example: "1000",
              impact: "Performance",
              security: "Critique pour la stabilité"
            },
            {
              key: "NODE_ENV",
              type: "string",
              defaultValue: "production",
              description: "Environnement d'exécution",
              required: true,
              validation: "development|production|test",
              example: "production",
              impact: "Comportement application",
              security: "Critique"
            }
          ],
          documentation: {
            setup: "Configuration via fichier .env ou variables d'environnement",
            warnings: ["Ne pas exposer le port en direct sur Internet", "Configurer un reverse proxy"],
            bestPractices: [
              "Utiliser des secrets pour les données sensibles",
              "Vérifier les permissions des fichiers de configuration"
            ]
          }
        },
        {
          id: "database",
          name: "Configuration Base de Données",
          description: "Paramètres de connexion à la base de données",
          environment: "Toutes",
          parameters: [
            {
              key: "DB_HOST",
              type: "string",
              defaultValue: "localhost",
              description: "Hôte de la base de données",
              required: true,
              validation: "URL valide",
              example: "db.company.com",
              impact: "Connectivité DB",
              security: "Interne uniquement"
            },
            {
              key: "DB_PORT",
              type: "number",
              defaultValue: "5432",
              description: "Port de la base de données",
              required: true,
              validation: "1024-65535",
              example: "5432",
              impact: "Connectivité DB",
              security: "Interne uniquement"
            }
          ],
          documentation: {
            setup: "Configuration via fichier .env et secrets Kubernetes",
            warnings: ["Ne jamais stocker les mots de passe en clair", "Utiliser SSL pour les connexions"],
            bestPractices: [
              "Utiliser des pools de connexions",
              "Configurer des timeouts appropriés"
            ]
          }
        }
      ]
    },
    {
      id: "security",
      name: "Configuration Sécurité",
      icon: "Shield",
      description: "Paramètres de sécurité et authentification",
      configurations: [
        {
          id: "auth",
          name: "Configuration Authentification",
          description: "Paramètres d'authentification et autorisation",
          environment: "Toutes",
          parameters: [
            {
              key: "JWT_SECRET",
              type: "string",
              defaultValue: null,
              description: "Clé secrète pour les tokens JWT",
              required: true,
              validation: "Minimum 32 caractères",
              example: "****",
              impact: "Sécurité Auth",
              security: "Hautement sensible"
            },
            {
              key: "SESSION_TIMEOUT",
              type: "number",
              defaultValue: "3600",
              description: "Durée de session en secondes",
              required: true,
              validation: "300-86400",
              example: "3600",
              impact: "Expérience utilisateur",
              security: "Modéré"
            }
          ],
          documentation: {
            setup: "Utiliser des secrets pour stocker les clés",
            warnings: ["Rotation régulière des clés", "Ne jamais exposer les secrets"],
            bestPractices: [
              "Utiliser des clés fortes",
              "Implémenter une politique de rotation"
            ]
          }
        }
      ]
    },
    {
      id: "integration",
      name: "Configuration Intégrations",
      icon: "Globe",
      description: "Paramètres des services externes",
      configurations: [
        {
          id: "email",
          name: "Configuration Email",
          description: "Paramètres du service d'envoi d'emails",
          environment: "Production",
          parameters: [
            {
              key: "SMTP_HOST",
              type: "string",
              defaultValue: null,
              description: "Serveur SMTP",
              required: true,
              validation: "URL valide",
              example: "smtp.company.com",
              impact: "Service Email",
              security: "Interne uniquement"
            },
            {
              key: "SMTP_PORT",
              type: "number",
              defaultValue: "587",
              description: "Port SMTP",
              required: true,
              validation: "1-65535",
              example: "587",
              impact: "Service Email",
              security: "Interne uniquement"
            }
          ],
          documentation: {
            setup: "Configuration via console d'administration",
            warnings: ["Vérifier les limites d'envoi", "Configurer SPF/DKIM"],
            bestPractices: [
              "Utiliser TLS",
              "Monitorer les taux de délivrance"
            ]
          }
        }
      ]
    }
  ]
};

// Composants UI de base
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Composant de paramètre de configuration
const ParameterDetail = ({ parameter, isEditing }) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(parameter.key);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-start space-x-3">
          <Key className="h-5 w-5 text-blue-500 mt-1" />
          <div>
            <div className="flex items-center space-x-2">
              <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {parameter.key}
              </code>
              <button
                onClick={copyToClipboard}
                className="p-1 hover:bg-gray-200 rounded"
              >
                {isCopied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">{parameter.description}</p>
          </div>
        </div>
        {parameter.required && (
          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
            Requis
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="font-medium text-gray-700">Type:</span>
          <span className="ml-2">{parameter.type}</span>
        </div>
        {parameter.defaultValue !== null && (
          <div>
            <span className="font-medium text-gray-700">Défaut:</span>
            <code className="ml-2 bg-gray-100 px-2 py-1 rounded">
              {parameter.defaultValue}
            </code>
          </div>
        )}
        {parameter.validation && (
          <div>
            <span className="font-medium text-gray-700">Validation:</span>
            <span className="ml-2">{parameter.validation}</span>
          </div>
        )}
        {parameter.example && (
          <div>
            <span className="font-medium text-gray-700">Exemple:</span>
            <code className="ml-2 bg-gray-100 px-2 py-1 rounded">
              {parameter.example}
            </code>
          </div>
        )}
      </div>

      {(parameter.impact || parameter.security) && (
        <div className="mt-4 flex flex-wrap gap-4">
          {parameter.impact && (
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <span className="text-sm text-gray-600">{parameter.impact}</span>
            </div>
          )}
          {parameter.security && (
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-red-500" />
              <span className="text-sm text-gray-600">{parameter.security}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Composant de documentation de configuration


const ConfigurationDetail = ({ config }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <Card className="mb-6">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50"
        onClick={handleToggle}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            {config.icon && (
              <div className="p-2 bg-blue-50 rounded-lg">
                <Settings className="h-5 w-5 text-blue-500" />
              </div>
            )}
            <div>
              <CardTitle>{config.name}</CardTitle>
              <p className="text-sm text-gray-600">{config.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm bg-gray-100 text-gray-700 px-2 py-1 rounded">
              {config.environment}
            </span>
            <button 
              className="p-2 hover:bg-gray-100 rounded-full transition-colors" 
              onClick={handleToggle}
            >
              {isExpanded ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-6">
            {/* Section des paramètres */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Paramètres de Configuration
              </h4>
              <div className="space-y-4">
                {config.parameters.map((parameter) => (
                  <ParameterDetail
                    key={parameter.key}
                    parameter={parameter}
                  />
                ))}
              </div>
            </div>

            {/* Section de documentation */}
            {config.documentation && (
              <div className="pt-6 border-t">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  Guide d'Implémentation
                </h4>
                
                <div className="space-y-4">
                  {/* Section configuration */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">
                      Configuration
                    </h5>
                    <p className="text-sm text-gray-600">
                      {config.documentation.setup}
                    </p>
                  </div>

                  {/* Section avertissements */}
                  {config.documentation.warnings && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        Points d'Attention
                      </h5>
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <div className="space-y-2">
                          {config.documentation.warnings.map((warning, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />
                              <span className="text-sm text-gray-600">{warning}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section bonnes pratiques */}
                  {config.documentation.bestPractices && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        Bonnes Pratiques
                      </h5>
                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="space-y-2">
                          {config.documentation.bestPractices.map((practice, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <Check className="h-4 w-4 text-green-500 mt-0.5" />
                              <span className="text-sm text-gray-600">{practice}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Composant de catégorie de configuration
const CategorySection = ({ category }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const IconComponent = {
    Server: Server,
    Shield: Shield,
    Globe: Globe,
    Database: Database,
    Mail: Mail
  }[category.icon] || Settings;

  return (
    <div className="mb-8">
      <div 
        className="flex items-center space-x-4 mb-6 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="p-3 bg-blue-50 rounded-lg">
          <IconComponent className="h-6 w-6 text-blue-500" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{category.name}</h2>
          <p className="text-gray-600">{category.description}</p>
        </div>
        <button className="ml-auto p-2 hover:bg-gray-100 rounded-full">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-6 pl-14">
          {category.configurations.map((config) => (
            <ConfigurationDetail
              key={config.id}
              config={config}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Composant principal de documentation de configuration
const DocumentConfiguration = () => {
  const [configData, setConfigData] = useState(CONFIGURATION_DOCUMENTATION);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {configData.metadata.title}
            </h1>
            <p className="mt-1 text-gray-600">
              Version {configData.metadata.version} - 
              Dernière mise à jour : {configData.metadata.lastUpdate}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center space-x-2"
          >
            {isEditing ? (
              <>
                <Save className="h-4 w-4" />
                <span>Sauvegarder</span>
              </>
            ) : (
              <>
                <Edit className="h-4 w-4" />
                <span>Modifier</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher une configuration..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Guide rapide */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <HelpCircle className="h-5 w-5 text-blue-500" />
            <span>Guide d'Utilisation</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-blue max-w-none">
            <p className="text-gray-600">
              Cette documentation détaille toutes les options de configuration disponibles dans le système. 
              Chaque configuration est organisée par catégorie et contient des informations détaillées sur :
            </p>
            <ul className="mt-4 space-y-2">
              <li className="flex items-center space-x-2">
                <Key className="h-4 w-4 text-blue-500" />
                <span>Les paramètres et leurs valeurs acceptées</span>
              </li>
              <li className="flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                <span>Les points d'attention et impacts potentiels</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="h-4 w-4 text-green-500" />
                <span>Les bonnes pratiques recommandées</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Catégories de configuration */}
      <div className="space-y-8">
        {configData.categories
          .filter(category =>
            category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            category.configurations.some(config =>
              config.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              config.parameters.some(param =>
                param.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                param.description.toLowerCase().includes(searchTerm.toLowerCase())
              )
            )
          )
          .map((category) => (
            <CategorySection 
              key={category.id} 
              category={category} 
            />
          ))}
      </div>

      {/* Modal d'ajout de configuration */}
      {isEditing && (
        <div className="fixed bottom-4 right-4">
          <Button
            onClick={() => {
              console.log('Add new configuration');
            }}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter une configuration</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default DocumentConfiguration;